import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Home,
  Package,
  Users,
  FileText,
  Receipt,
  ChevronLeft,
  ChevronRight,
  Menu,
  CheckCircle,
  DollarSign,
  MapPin,
  Image
} from 'lucide-react';

const ModernSidebar = ({ mobileMenuOpen, setMobileMenuOpen }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Load sidebar state from localStorage
  useEffect(() => {
    const savedState = localStorage.getItem('sidebarCollapsed');
    if (savedState !== null) {
      setIsCollapsed(JSON.parse(savedState));
    }
  }, []);

  // Save sidebar state to localStorage and notify other components
  const toggleSidebar = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    localStorage.setItem('sidebarCollapsed', JSON.stringify(newState));
    // Dispatch custom event to sync with TopNavBar
    window.dispatchEvent(new CustomEvent('sidebarToggle', { detail: newState }));
  };

  const menuItems = [
    {
      icon: Home,
      label: 'Dashboard',
      path: '/',
      active: location.pathname === '/'
    },
    {
      icon: Package,
      label: 'Products',
      path: '/sales/products',
      active: location.pathname.includes('/products')
    },
    {
      icon: Image,
      label: 'Gallery',
      path: '/gallery',
      active: location.pathname.includes('/gallery')
    },
    {
      icon: Users,
      label: 'Customers',
      path: '/sales/customers',
      active: location.pathname.includes('/customers')
    },
    {
      icon: FileText,
      label: 'Estimates',
      path: '/sales/estimates',
      active: location.pathname.includes('/estimates')
    },
    {
      icon: CheckCircle,
      label: 'Closed Orders',
      path: '/sales/closed-orders',
      active: location.pathname.includes('/closed-orders')
    },
    {
      icon: Receipt,
      label: 'Invoices',
      path: '/sales/invoices',
      active: location.pathname.includes('/invoices')
    },
    {
      icon: DollarSign,
      label: 'Expenses',
      path: '/sales/expenses',
      active: location.pathname.includes('/expenses')
    },
    {
      icon: MapPin,
      label: 'Site Visits',
      path: '/sales/site-visits',
      active: location.pathname.includes('/site-visits')
    }
  ];

  const handleNavigation = (path) => {
    navigate(path);
    // Close mobile menu after navigation
    if (setMobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  };

  return (
    <div
      className={`
        bg-[#0A5F38] border-r border-white/10 transition-all duration-300 ease-in-out z-40 flex-shrink-0 flex flex-col shadow-lg
        fixed left-0
        transform ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0
        ${isCollapsed ? 'w-20' : 'w-64'
        }
      `}
      style={{
        top: user?.isSandbox ? 'var(--sandbox-banner-height)' : '0',
        height: user?.isSandbox ? 'calc(100vh - var(--sandbox-banner-height))' : '100vh'
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/10 flex-shrink-0">
        {isCollapsed ? (
          <div className="flex flex-col items-center w-full space-y-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center overflow-hidden">
              <img
                src="https://5.imimg.com/data5/TQ/IX/FZ/SELLER-66617534/logo-90x90.jpg"
                alt="IDEALTRENDZ Logo"
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className="w-full h-full bg-[#C5A57D] rounded-lg flex items-center justify-center" style={{ display: 'none' }}>
                <span className="text-white font-bold text-sm">IT</span>
              </div>
            </div>
            <button
              onClick={toggleSidebar}
              className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
            >
              <ChevronRight className="h-4 w-4 text-white/70" />
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center overflow-hidden">
                <img
                  src="https://5.imimg.com/data5/TQ/IX/FZ/SELLER-66617534/logo-90x90.jpg"
                  alt="IDEALTRENDZ Logo"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="w-full h-full bg-[#C5A57D] rounded-lg flex items-center justify-center" style={{ display: 'none' }}>
                  <span className="text-white font-bold text-sm">IT</span>
                </div>
              </div>
              <div>
                <h1 className="text-lg font-bold text-white">IDEALTRENDZ</h1>
                <p className="text-xs text-white/80 uppercase tracking-wide">EMPLOYEE PORTAL</p>
              </div>
            </div>
            <button
              onClick={toggleSidebar}
              className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
            >
              <ChevronLeft className="h-4 w-4 text-white/70" />
            </button>
          </>
        )}
      </div>

      {/* Navigation Menu */}
      <nav className="mt-6 px-3 flex-1 overflow-y-auto no-scrollbar">
        <div className="space-y-2">
          {menuItems.map((item) => {
            const IconComponent = item.icon;
            return (
              <button
                key={item.path}
                onClick={() => handleNavigation(item.path)}
                className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-colors group ${item.active
                  ? 'bg-[#C5A57D] text-[#0A5F38] font-bold'
                  : 'text-white/90 hover:bg-white/10 hover:text-white'
                  } ${isCollapsed ? 'justify-center' : ''}`}
                title={isCollapsed ? item.label : ''}
              >
                <IconComponent
                  className={`h-5 w-5 ${item.active ? 'text-[#0A5F38]' : 'text-white/80 group-hover:text-white'
                    }`}
                />
                {!isCollapsed && (
                  <span className="font-medium text-sm">{item.label}</span>
                )}
              </button>
            );
          })}
        </div>
      </nav>


    </div>
  );
};

export default ModernSidebar;



